import { LANGUAGE_ACTORS } from '../../supabase/functions/_shared/languageActors';

// Curated pre-rendered MetaPerson GLBs hosted in Supabase Storage at
// `avatars/prerendered/`. Rafa uploads the GLBs + matching PNG posters
// out-of-band via the Supabase Dashboard; filenames are the contract.
//
// Since 24/08 the LIVE catalog comes from the `avatar_catalog` table via
// `useAvatarCatalog()` — this array stays as the SEED/fallback so the avatar
// picker never depends on the network to render. Do NOT delete it.
//
// `name` is a placeholder human label — not i18n'd today, deferred to a
// follow-up once the curated set is finalized.
//
// `posterUrl` points to the 2D PNG companion of each .glb (same path,
// .glb → .png). Posters are ~1 MB each (vs ~15 MB for the .glb), so the
// onboarding picker can render an instant 2D grid and only fetch the
// heavy .glb when the user actually selects an avatar.

export interface PrerenderedAvatar {
  id: string;
  name: string;
  gender: "male" | "female";
  glbUrl: string;
  posterUrl: string;
}

const BASE_URL =
  "https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/avatars/prerendered";

// The filename contract, in ONE place: `file_base` → sibling .glb / .png under
// `avatars/prerendered/`. Both the seed array below and the `avatar_catalog`
// rows go through this — a divergence would break the poster-first grid.
export function buildPrerenderedUrls(fileBase: string): {
  glbUrl: string;
  posterUrl: string;
} {
  return {
    glbUrl: BASE_URL + "/" + fileBase + ".glb",
    posterUrl: BASE_URL + "/" + fileBase + ".png",
  };
}

const make = (
  id: string,
  name: string,
  gender: "male" | "female",
  file: string,
): PrerenderedAvatar => ({
  id,
  name,
  gender,
  ...buildPrerenderedUrls(file),
});

// Verified against the eleven active official catalogue rows on 2026-11-04.
export const PRERENDERED_AVATARS: PrerenderedAvatar[] = Object.values(LANGUAGE_ACTORS)
  .map(actor => make(actor.id, actor.name, actor.sex, actor.id));

// Shape of a row read from `avatar_catalog` (fields the picker consumes).
// Values are `unknown` on purpose: the table is founder-editable, so a
// malformed row must be DROPPED here, never allowed to break the grid.
export interface AvatarCatalogRow {
  id?: unknown;
  name?: unknown;
  gender?: unknown;
  file_base?: unknown;
}

// Pure mapping DB rows → picker avatars. Invalid rows are skipped:
//  - id/name/file_base must be non-empty strings, gender must be male|female;
//  - file_base lands in a URL path segment, so it is restricted to a safe
//    charset (no traversal, no separators).
export function mapCatalogRows(rows: unknown): PrerenderedAvatar[] {
  if (!Array.isArray(rows)) return [];
  const out: PrerenderedAvatar[] = [];
  for (const raw of rows) {
    const r = (raw ?? {}) as AvatarCatalogRow;
    const id = typeof r.id === "string" ? r.id.trim() : "";
    const name = typeof r.name === "string" ? r.name.trim() : "";
    const gender =
      r.gender === "male" || r.gender === "female" ? r.gender : null;
    const fileBase = typeof r.file_base === "string" ? r.file_base.trim() : "";
    if (!id || !name || !gender || !fileBase) continue;
    if (!/^[A-Za-z0-9_-]+$/.test(fileBase)) continue;
    out.push({ id, name, gender, ...buildPrerenderedUrls(fileBase) });
  }
  return out;
}

export type GenderFilter = "male" | "female" | "other" | null;

// Filters a catalog list by gender. Callers that already hold the live list
// from `useAvatarCatalog()` pass it as the second argument; the default keeps
// the pre-24/08 behavior (seed array) for anyone calling it bare.
export function getPrerenderedByGender(
  gender: GenderFilter,
  avatars: PrerenderedAvatar[] = PRERENDERED_AVATARS,
): PrerenderedAvatar[] {
  if (gender === "male" || gender === "female") {
    return avatars.filter((a) => a.gender === gender);
  }
  return avatars;
}
